import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  FrameLabel,
  FrameWrapper,
  PictureWrapper,
  PictureImage,
  PictureDescription,
  NoteWrapper,
  StyleDefaultImage,
  TextareaWithCountWrapper,
} from './styles/FrameStyles';
import TrashCanButton from '../Parts/TrashCanButton';
import { convertISOFormatToDate } from '../../lib/tools';
import TextareaWithCount from '../Parts/TextareaWithCount';
import DisplayTags from './DisplayTags';
import useFrameAutoSave from './hooks/useFrameAutoSave';
import useBlockNavigation from './hooks/useBlockNavigation';
import ConfirmDialog from '../Parts/ConfirmationDialog';

/**
 * フレームの画像を表示するコンポーネント
 * @prams {string} fileUrl - 画像URL
 * @prams {string|number} frameId - フレームID
 * @returns
 */
const PictureContent = ({ fileUrl, frameId }) => {
  if (fileUrl) {
    return <PictureImage alt="IMG" src={fileUrl} />;
  }
  if (!frameId) {
    return (
      <>
        <StyleDefaultImage />
        <PictureDescription>
          <FormattedMessage id="report.edit.info.picture.placeholder" />
        </PictureDescription>
      </>
    );
  }
  return (
    <>
      <StyleDefaultImage />
      <PictureDescription style={{ fontSize: '18px' }}>
        <FormattedMessage id="report.edit.info.picture.noImage" />
      </PictureDescription>
    </>
  );
};

PictureContent.propTypes = {
  fileUrl: PropTypes.string,
  frameId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PictureContent.defaultProps = {
  fileUrl: null,
  frameId: null,
};

/**
 * Web報告書のフレームを表示するコンポーネント
 * @component
 */
const Frame = ({
  reportId,
  frame,
  onChangeNote,
  onDeleteFrame,
  openSelectMediaDialog,
  setConflictFrameId,
  isSingleFrame,
  updateAutoSaveStatus,
}) => {
  const intl = useIntl();

  const { workRecord, tags, freeTag, note } = frame ?? {};
  const { fileUrl } = workRecord ?? {};

  const dateTime =
    workRecord?.fileCreatedAt &&
    convertISOFormatToDate(workRecord?.fileCreatedAt);

  const { isFrameSaving } = useFrameAutoSave(
    reportId,
    frame,
    setConflictFrameId,
    updateAutoSaveStatus,
  );
  useBlockNavigation(isFrameSaving);

  // 削除確認ダイアログ関係
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const handleConfirm = () => {
    onDeleteFrame();
    setOpenConfirmDialog(false);
  };

  return (
    <>
      <FrameWrapper>
        <PictureWrapper
          onClick={openSelectMediaDialog}
          workRecord={frame?.workRecord}
        >
          <PictureContent fileUrl={fileUrl} frameId={frame?.id} />
        </PictureWrapper>
        <NoteWrapper>
          <FrameLabel>{dateTime}</FrameLabel>
          <FrameLabel>
            <DisplayTags tags={tags} freeTag={freeTag} noDataText="" />
          </FrameLabel>
          <TextareaWithCountWrapper>
            <TextareaWithCount
              value={note ?? ''}
              maxLength={140}
              width="494px"
              height="88px"
              disabled={frame?.id === undefined}
              onChange={value => onChangeNote(value)}
              placeholder={intl.formatMessage({
                id: 'report.edit.frame.placeholder',
              })}
            />
          </TextareaWithCountWrapper>
        </NoteWrapper>
        <TrashCanButton
          disabled={frame?.id === undefined || isSingleFrame}
          size="32px"
          onClick={() => setOpenConfirmDialog(true)}
        />
      </FrameWrapper>
      {openConfirmDialog && (
        <ConfirmDialog
          headerText={
            <FormattedMessage id="report.edit.deleteConfirmDialog.title" />
          }
          messageText={
            <FormattedMessage id="report.edit.deleteConfirmDialog.message" />
          }
          onCancel={() => setOpenConfirmDialog(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

Frame.propTypes = {
  reportId: PropTypes.number.isRequired,
  frame: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  onChangeNote: PropTypes.func.isRequired,
  onDeleteFrame: PropTypes.func.isRequired,
  openSelectMediaDialog: PropTypes.func.isRequired,
  setConflictFrameId: PropTypes.func.isRequired,
  isSingleFrame: PropTypes.bool.isRequired,
  updateAutoSaveStatus: PropTypes.func.isRequired,
};

Frame.defaultProps = {
  frame: null,
};

export default Frame;
